import { DataIntegrity } from "@corpuscomplete/core/integrity/data-integrity";
import { Food } from "@corpuscomplete/core/nutrition/food";
import { Unit } from "@corpuscomplete/core/common/unit";
import { capitalizeFirst, must } from "@corpuscomplete/core/utils";

export namespace ServingParser {
  export type Result = {
    name: string;
    defaultAmount: number;
    modifiers: string[];
    mass: {
      unitKey: Unit.Key<"mass">;
      quantity: number;
    } | null;
    volume: {
      unitKey: Unit.Key<"volume">;
      quantity: number;
    } | null;
  };

  const float = "\\d*[\\.,]?\\d+";
  const fraction = "\\d+\\/\\d+";
  const wsp = "\\s+";
  const optWsp = "\\s*";
  const amountAndName = `(?:(?:about|approximately|around|roughly)\\s+)?(${fraction}${wsp}|${float}${optWsp})([^\\d]+)`;

  const cleanInputText = (text: string): string => {
    return text.trim();
  };

  type ServingParser = (servingText: string) => Result | undefined;

  type UnitContext = {
    nameToInfoMap: Map<string, Unit.Info>;
  };

  const isMassUnitKey = (unitKey: Unit.Key): unitKey is Unit.Key<"mass"> => {
    return Unit.keySchema("mass").safeParse(unitKey).success;
  };
  const isVolumeUnitKey = (unitKey: Unit.Key): unitKey is Unit.Key<"volume"> => {
    return Unit.keySchema("volume").safeParse(unitKey).success;
  };

  export type Value = ReturnType<typeof create>;

  export const create = (allUnits: Unit.Info[]) => {
    const unitContext = createUnitContext(allUnits);
    const matchers = [
      doubleParensWithMultipleUnitsParser(unitContext),
      doubleParensWithUnitsParser(unitContext),
      parensWithMultipleUnitsParser(unitContext),
      parensWithUnitsParser(unitContext),
      // parensNoUnitsParser(unitContext),
      commaWithUnitsParser(unitContext),
      // commaNoUnitsParser(unitContext),
      basicParser(unitContext),
    ];

    return {
      parse: (servingText: string) => {
        for (const matcher of matchers) {
          const result = matcher(servingText);
          if (result) {
            return result;
          }
        }
        return undefined;
      },
    };
  };

  const createUnitContext = (
    allUnits: Unit.Info[],
    excludeImperial: boolean = true,
  ): UnitContext => {
    const matcherUnits = allUnits.filter((unit) => {
      if (excludeImperial && unit.key.endsWith("_imp")) {
        return false;
      }
      return unit.type === "mass" || unit.type === "volume";
    });

    const nameToInfoMap = new Map<string, Unit.Info>();
    for (const unit of matcherUnits) {
      nameToInfoMap.set(unit.defaultName.toLowerCase(), unit);
      for (const alias of unit.defaultAliases) {
        nameToInfoMap.set(alias.toLowerCase(), unit);
      }
    }

    return {
      nameToInfoMap,
    };
  };

  function servingCandidateToUnitAndSuffix(unitContext: UnitContext, servingName: string) {
    const cleanedName = servingName.trim().toLowerCase().replace(/\.$/, "");
    const unit = unitContext.nameToInfoMap.get(cleanedName);
    if (unit) {
      return { unit, suffix: "" };
    }

    const commaSplit = cleanedName.split(",");
    if (commaSplit.length > 1 && commaSplit[0]) {
      const altUnit = unitContext.nameToInfoMap.get(commaSplit[0]);
      if (altUnit) {
        return {
          unit: altUnit,
          suffix: commaSplit
            .slice(1)
            .map((s) => s.trim())
            .join(", "),
        };
      }
    }

    const targetWords = cleanedName.split(" ");
    let bestMatch: { unit: Unit.Info; unitWordCount: number; suffixWords: string[] } | null = null;

    for (const [unitName, unit] of unitContext.nameToInfoMap) {
      const unitWords = unitName.split(" ");

      if (targetWords.length >= unitWords.length) {
        const matches = unitWords.every((word, i) => targetWords[i] === word);
        if (matches && (!bestMatch || unitWords.length > bestMatch.unitWordCount)) {
          bestMatch = {
            unit,
            unitWordCount: unitWords.length,
            suffixWords: targetWords.slice(unitWords.length),
          };
        }
      }
    }

    if (bestMatch) {
      return {
        unit: bestMatch.unit,
        suffix: bestMatch.suffixWords.join(" "),
      };
    }
    return null;
  }

  type ServingCandidate = {
    amount: number;
    name: string;
  };

  function parseServingCandidates(
    unitContext: UnitContext,
    candidates: ServingCandidate[],
    nameSuffix = "",
  ): Result | undefined {
    let massNeedsNormalization: {
      unit: Unit.Info;
      unitKey: Unit.Key<"mass">;
      quantity: number;
    } | null = null;
    let volumeNeedsNormalization: {
      unit: Unit.Info;
      unitKey: Unit.Key<"volume">;
      quantity: number;
    } | null = null;
    let primaryType: "mass" | "volume" | null = null;
    let primary: (ServingCandidate & { modifiers: string[] }) | null = null;
    const seenNames = new Set<string>();

    for (const { amount, name } of candidates) {
      // Skips redundant names
      if (seenNames.has(name)) {
        continue;
      }
      seenNames.add(name);

      const unitAndSuffix = servingCandidateToUnitAndSuffix(unitContext, name);
      if (!unitAndSuffix) {
        if (!primary || primaryType !== null) {
          if (name === name.toUpperCase()) {
            primary = { amount, name: name.toLowerCase(), modifiers: [] };
          } else {
            primary = { amount, name, modifiers: [] };
          }
          primaryType = null;
        }
      } else if (isMassUnitKey(unitAndSuffix.unit.key)) {
        if (!primary) {
          primary = {
            amount,
            name: unitAndSuffix.unit.defaultName,
            modifiers: parseModifiers(unitAndSuffix.suffix),
          };
          primaryType = "mass";
        }
        if (!massNeedsNormalization || massNeedsNormalization.unitKey === "g") {
          massNeedsNormalization = {
            unitKey: unitAndSuffix.unit.key,
            unit: unitAndSuffix.unit,
            quantity: amount,
          };
        }
      } else if (isVolumeUnitKey(unitAndSuffix.unit.key)) {
        const shouldUpdateVolume =
          !volumeNeedsNormalization || volumeNeedsNormalization.unitKey === "mL";

        if (
          !primary ||
          primaryType === "mass" ||
          (primaryType === "volume" && shouldUpdateVolume)
        ) {
          primary = {
            amount,
            name: unitAndSuffix.unit.defaultName,
            modifiers: parseModifiers(unitAndSuffix.suffix),
          };
          primaryType = "volume";
        }
        if (shouldUpdateVolume) {
          volumeNeedsNormalization = {
            unitKey: unitAndSuffix.unit.key,
            unit: unitAndSuffix.unit,
            quantity: amount,
          };
        }
      }
    }

    if (!primary) {
      return undefined;
    }

    let name = primary.name;
    let modifiers = primary.modifiers;
    if (primary.modifiers.length > 0) {
      modifiers = [...modifiers, ...parseModifiers(nameSuffix)];
    } else {
      name += nameSuffix;
    }

    return {
      name,
      defaultAmount: primary.amount,
      modifiers,
      mass: massNeedsNormalization
        ? {
            unitKey: massNeedsNormalization.unitKey,
            quantity: massNeedsNormalization.quantity / primary.amount,
          }
        : null,
      volume: volumeNeedsNormalization
        ? {
            unitKey: volumeNeedsNormalization.unitKey,
            quantity: volumeNeedsNormalization.quantity / primary.amount,
          }
        : null,
    };
  }

  function parseModifiers(suffix: string): string[] {
    if (!suffix) {
      return [];
    }
    let cleaned = suffix.trim();
    if (cleaned.startsWith(",")) {
      cleaned = cleaned.slice(1).trim();
    }
    if (cleaned.startsWith("(") && cleaned.endsWith(")")) {
      cleaned = cleaned.slice(1, -1).trim();
    }
    if (!cleaned) {
      return [];
    }
    return cleaned
      .split(",")
      .map((m) => m.trim())
      .filter((m) => m.length > 0);
  }

  const basicParser = (unitContext: UnitContext): ServingParser => {
    return (servingText) => {
      const match = cleanInputText(servingText).match(new RegExp(`^${amountAndName}(.*)$`));
      if (match) {
        return parseServingCandidates(
          unitContext,
          [{ amount: parseFloatOrFraction(match[1]), name: must(match[2]) }],
          match[3],
        );
      }
    };
  };

  // const parensNoUnitsParser = (unitContext: UnitContext): ServingParser => {
  //   return (servingText) => {
  //     const match = cleanInputText(servingText).match(
  //       new RegExp(`^${amountAndName}(.*)$`),
  //     );
  //     if (match) {
  //       return parseServingCandidates(
  //         unitContext,
  //         [{ amount: parseFloatOrFraction(match[1]), name: must(match[2]) }],
  //         match[3],
  //       );
  //     }
  //   };
  // };

  const parensWithUnitsParser = (unitContext: UnitContext): ServingParser => {
    return (servingText) => {
      const match = cleanInputText(servingText).match(
        new RegExp(`^${amountAndName}${wsp}\\(${amountAndName}\\)(.*)$`),
      );
      if (match) {
        return parseServingCandidates(
          unitContext,
          [
            { amount: parseFloatOrFraction(match[1]), name: must(match[2]) },
            { amount: parseFloatOrFraction(match[3]), name: must(match[4]) },
          ],
          match[5],
        );
      }
    };
  };

  const parensWithMultipleUnitsParser = (unitContext: UnitContext): ServingParser => {
    return (servingText) => {
      const match = cleanInputText(servingText).match(
        new RegExp(
          `^${amountAndName}${wsp}\\(${amountAndName}${optWsp}(?:/|or)${optWsp}${amountAndName}\\)(.*)$`,
        ),
      );
      if (match) {
        return parseServingCandidates(
          unitContext,
          [
            { amount: parseFloatOrFraction(match[1]), name: must(match[2]) },
            { amount: parseFloatOrFraction(match[3]), name: must(match[4]) },
            { amount: parseFloatOrFraction(match[5]), name: must(match[6]) },
          ],
          match[7],
        );
      }
    };
  };

  const doubleParensWithUnitsParser = (unitContext: UnitContext): ServingParser => {
    return (servingText) => {
      const match = cleanInputText(servingText).match(
        new RegExp(
          `^${amountAndName}${wsp}\\(${amountAndName}\\)${wsp}\\(${amountAndName}\\)(.*)$`,
        ),
      );
      if (match) {
        return parseServingCandidates(
          unitContext,
          [
            { amount: parseFloatOrFraction(match[1]), name: must(match[2]) },
            { amount: parseFloatOrFraction(match[3]), name: must(match[4]) },
            { amount: parseFloatOrFraction(match[5]), name: must(match[6]) },
          ],
          match[7],
        );
      }
    };
  };

  const doubleParensWithMultipleUnitsParser = (unitContext: UnitContext): ServingParser => {
    return (servingText) => {
      const match = cleanInputText(servingText).match(
        new RegExp(
          `^${amountAndName}${wsp}\\(${amountAndName}${optWsp}(?:/|or)${optWsp}${amountAndName}\\)${wsp}\\(${amountAndName}\\)(.*)$`,
        ),
      );
      if (match) {
        return parseServingCandidates(
          unitContext,
          [
            { amount: parseFloatOrFraction(match[1]), name: must(match[2]) },
            { amount: parseFloatOrFraction(match[3]), name: must(match[4]) },
            { amount: parseFloatOrFraction(match[5]), name: must(match[6]) },
            { amount: parseFloatOrFraction(match[7]), name: must(match[8]) },
          ],
          match[9],
        );
      }
    };
  };

  // const commaNoUnitsParser = (unitContext: UnitContext): ServingParser => {
  //   return (servingText) => {
  //     const match = cleanInputText(servingText).match(
  //       new RegExp(`^${amountAndName},${wsp}(.*)$`),
  //     );
  //     if (match) {
  //       return parseServingCandidates(
  //         unitContext,
  //         [{ amount: parseFloatOrFraction(match[1]), name: must(match[2]) }],
  //         `, ${match[3]}`,
  //       );
  //     }
  //   };
  // };

  const commaWithUnitsParser = (unitContext: UnitContext): ServingParser => {
    return (servingText) => {
      const match = cleanInputText(servingText).match(
        new RegExp(`^${amountAndName},${wsp}${amountAndName}(.*)$`),
      );
      if (match) {
        return parseServingCandidates(
          unitContext,
          [
            { amount: parseFloatOrFraction(match[1]), name: must(match[2]) },
            { amount: parseFloatOrFraction(match[3]), name: must(match[4]) },
          ],
          match[5],
        );
      }
    };
  };

  const parseFloatOrFraction = (str: string | undefined): number => {
    if (!str) {
      throw new Error(`Undefined value`);
    }
    // Handle comma decimal separators by converting to period
    const normalizedStr = str.replace(",", ".");

    const [num, denom] = normalizedStr.split("/");
    if (!num) {
      throw new Error(`Undefined numerator`);
    }
    if (denom) {
      return parseFloat(num) / parseFloat(denom);
    }
    return parseFloat(normalizedStr);
  };
}

export namespace FoodImportIntegrity {
  const DEFAULT_BANNED_TOKENS = [
    "generic",
    "unspecified",
    "not specified",
    "not further specified",
    "placeholder",
  ] as const;

  const TITLECASE_LOWER_WORDS = new Set([
    "a",
    "an",
    "and",
    "as",
    "at",
    "by",
    "for",
    "from",
    "in",
    "of",
    "on",
    "or",
    "the",
    "to",
    "with",
    "without",
  ]);

  function isLikelyAcronym(token: string): boolean {
    const letters = token.replace(/[^A-Za-z]/g, "");
    if (letters.length < 2) {
      return false;
    }
    return letters === letters.toUpperCase();
  }

  function titleCaseWord(
    word: string,
    forceCapitalize: boolean,
    preserveAcronyms: boolean,
  ): string {
    if (!/[A-Za-z]/.test(word)) return word;
    if (/[0-9]/.test(word)) return word;
    if (preserveAcronyms && isLikelyAcronym(word)) return word;

    const lower = word.toLowerCase();
    if (!forceCapitalize && TITLECASE_LOWER_WORDS.has(lower)) {
      return lower;
    }
    return capitalizeFirst(lower);
  }

  function titleCaseApostropheToken(
    token: string,
    forceCapitalize: boolean,
    preserveAcronyms: boolean,
  ): string {
    const contractionSuffixes = new Set([
      "s",
      "t",
      "d",
      "ll",
      "re",
      "ve",
      "m",
      "n",
      "em",
      "til",
      "cause",
    ]);
    const parts = token.split("'");
    return parts
      .map((part, idx) => {
        if (part.length === 0) return part;
        if (idx > 0 && contractionSuffixes.has(part.toLowerCase())) {
          return part.toLowerCase();
        }
        const isLast = idx === parts.length - 1;
        const boundaryForce = parts.length > 1 && (idx === 0 || isLast);
        const segmentForce = parts.length > 1 ? boundaryForce : forceCapitalize;
        return titleCaseWord(part, segmentForce, preserveAcronyms);
      })
      .join("'");
  }

  function titleCaseHyphenToken(
    token: string,
    forceCapitalize: boolean,
    preserveAcronyms: boolean,
  ): string {
    const parts = token.split("-");
    return parts
      .map((part, idx) => {
        const isFirst = idx === 0;
        const isLast = idx === parts.length - 1;
        const boundaryForce = parts.length > 1 && (isFirst || isLast);
        const segmentForce = parts.length > 1 ? boundaryForce : forceCapitalize;
        return titleCaseApostropheToken(part, segmentForce, preserveAcronyms);
      })
      .join("-");
  }

  function titleCaseToken(
    token: string,
    forceCapitalize: boolean,
    preserveAcronyms: boolean,
  ): string {
    const slashParts = token.split("/");
    return slashParts
      .map((part, idx) => {
        const isFirst = idx === 0;
        const isLast = idx === slashParts.length - 1;
        const boundaryForce = slashParts.length > 1 && (isFirst || isLast);
        const segmentForce = slashParts.length > 1 ? boundaryForce : forceCapitalize;
        return titleCaseHyphenToken(part, segmentForce, preserveAcronyms);
      })
      .join("/");
  }

  function defaultPreferredTitleCase(value: string, preserveAcronyms: boolean): string {
    const tokens = value.split(/\s+/).filter(Boolean);
    if (tokens.length === 0) {
      return value;
    }

    const out: string[] = [];
    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i] ?? "";
      const forceCapitalize = i === 0 || i === tokens.length - 1;

      const leading = token.match(/^[^A-Za-z0-9]*/)?.[0] ?? "";
      const trailing = token.match(/[^A-Za-z0-9]*$/)?.[0] ?? "";
      const core = token.slice(leading.length, token.length - trailing.length);

      if (!core) {
        out.push(token);
        continue;
      }

      out.push(`${leading}${titleCaseToken(core, forceCapitalize, preserveAcronyms)}${trailing}`);
    }

    return out.join(" ");
  }

  function normalizeSpaces(value: string, collapseWhitespace = true): string {
    if (!collapseWhitespace) {
      return value.trim();
    }
    return value.replace(/\s+/g, " ").trim();
  }

  function normalizeCasefold(value: string, collapseWhitespace = true): string {
    return normalizeSpaces(value, collapseWhitespace)
      .normalize("NFKC")
      .toLowerCase()
      .replace(/[\u2010-\u2015]/g, "-");
  }

  function normalizeModifiers(
    modifiers: readonly string[] | undefined,
    options?: { collapseWhitespace?: boolean; normalizeCase?: boolean },
  ): string[] {
    if (!modifiers || modifiers.length === 0) {
      return [];
    }

    const collapseWhitespace = options?.collapseWhitespace ?? true;
    const normalizeCase = options?.normalizeCase ?? true;
    const seen = new Set<string>();
    const out: string[] = [];
    for (const modifier of modifiers) {
      const normalized = normalizeSpaces(modifier, collapseWhitespace);
      if (!normalized) {
        continue;
      }
      const canonical = normalizeCase ? normalized.toLowerCase() : normalized;
      if (seen.has(canonical)) {
        continue;
      }
      seen.add(canonical);
      out.push(canonical);
    }
    return out.sort((a, b) => a.localeCompare(b));
  }

  function normalizeLabelKey(
    name: string,
    modifiers: readonly string[] | undefined,
    options?: { collapseWhitespace?: boolean; normalizeCase?: boolean },
  ): string {
    const collapseWhitespace = options?.collapseWhitespace ?? true;
    const normalizedName = normalizeCasefold(name, collapseWhitespace);
    const normalizedModifiers = normalizeModifiers(modifiers, options)
      .map((modifier) => normalizeCasefold(modifier, collapseWhitespace))
      .join("|");
    return `${normalizedName}||${normalizedModifiers}`;
  }

  export type RuleId =
    | "food.empty-name"
    | "food.non-ascii"
    | "food.forbidden-symbol"
    | "food.banned-token"
    | "food.invalid-modifier"
    | "food.duplicate-modifier"
    | "food.modifier-word-redundancy"
    | "food.normalized-label-collision"
    | "food.mixed-modifier-coverage"
    | "food.singleton-with-modifier"
    | "food.title-case-mismatch";

  export type Item = {
    name: string;
    modifiers?: readonly string[];
  };

  export type Patch = {
    name?: string;
    modifiers?: readonly string[];
  };

  export type RowRule<TItem extends Item> = DataIntegrity.RowRule<TItem, RuleId, Patch>;
  export type DatasetRule<TItem extends Item> = DataIntegrity.DatasetRule<TItem, RuleId, Patch>;

  export const CORE_FOOD_MODIFIER_VOCABULARY: readonly Food.Modifier[] =
    Food.modifierSchema.options;

  type CollisionRow<TItem extends Item> = {
    item: TItem;
    name: string;
    modifiers: readonly string[];
  };

  export namespace Rules {
    export function nameNotEmpty<TItem extends Item>(options?: {
      collapseWhitespace?: boolean;
    }): RowRule<TItem> {
      const collapseWhitespace = options?.collapseWhitespace ?? true;
      return {
        id: "food.empty-name",
        check: ({ item }) => {
          const name = normalizeSpaces(item.name, collapseWhitespace);
          if (name.length > 0) {
            return [];
          }
          return [
            {
              severity: "error",
              message: "Name is empty.",
            },
          ];
        },
      };
    }

    export function bannedToken<TItem extends Item>(options?: {
      bannedTokens?: readonly string[];
    }): RowRule<TItem> {
      const bannedTokens = options?.bannedTokens ?? DEFAULT_BANNED_TOKENS;
      return {
        id: "food.banned-token",
        check: ({ item }) => {
          const lower = item.name.toLowerCase();
          const found = bannedTokens.some((token) => lower.includes(token.toLowerCase()));
          if (!found) {
            return [];
          }
          return [
            {
              severity: "error",
              message: "Name contains banned metadata tokens.",
            },
          ];
        },
      };
    }

    export function nonAscii<TItem extends Item>(options?: { pattern?: RegExp }): RowRule<TItem> {
      const pattern = options?.pattern ?? /[^\x20-\x7E]/;
      return {
        id: "food.non-ascii",
        check: ({ item }) => {
          if (!pattern.test(item.name)) {
            return [];
          }
          return [
            {
              severity: "warn",
              message: "Name contains non-ASCII characters.",
            },
          ];
        },
      };
    }

    export function forbiddenSymbol<TItem extends Item>(options?: {
      pattern?: RegExp;
    }): RowRule<TItem> {
      const pattern = options?.pattern ?? /[&<>]/;
      return {
        id: "food.forbidden-symbol",
        check: ({ item }) => {
          if (!pattern.test(item.name)) {
            return [];
          }
          return [
            {
              severity: "error",
              message: "Name contains forbidden symbols (&, <, >).",
            },
          ];
        },
      };
    }

    export function titleCaseMismatch<TItem extends Item>(options?: {
      collapseWhitespace?: boolean;
      preserveAcronyms?: boolean;
      preferredTitleCase?: (value: string) => string;
    }): RowRule<TItem> {
      const collapseWhitespace = options?.collapseWhitespace ?? true;
      const preserveAcronyms = options?.preserveAcronyms ?? true;
      const preferredTitleCase =
        options?.preferredTitleCase ??
        ((value: string) => defaultPreferredTitleCase(value, preserveAcronyms));

      return {
        id: "food.title-case-mismatch",
        check: ({ item }) => {
          const suggestedName = preferredTitleCase(item.name);
          if (
            normalizeSpaces(item.name, collapseWhitespace) ===
            normalizeSpaces(suggestedName, collapseWhitespace)
          ) {
            return [];
          }
          return [
            {
              severity: "warn",
              message: "Name differs from preferred title case.",
              suggestedPatch: { name: suggestedName },
            },
          ];
        },
      };
    }

    export function duplicateModifier<TItem extends Item>(options?: {
      collapseWhitespace?: boolean;
    }): RowRule<TItem> {
      const collapseWhitespace = options?.collapseWhitespace ?? true;
      return {
        id: "food.duplicate-modifier",
        check: ({ item }) => {
          const rawModifiers = item.modifiers ?? [];
          const normalized = rawModifiers
            .map((value) => normalizeSpaces(value, collapseWhitespace))
            .filter(Boolean);
          const uniqueNormalized = new Set(normalized.map((value) => value.toLowerCase()));
          const normalizedModifiers = normalizeModifiers(rawModifiers, {
            collapseWhitespace,
            normalizeCase: true,
          });

          const issues: DataIntegrity.RuleIssue<TItem, Patch>[] = [];
          if (normalized.length !== rawModifiers.length) {
            issues.push({
              severity: "warn",
              message: "Modifiers contain empty or whitespace-only values.",
              suggestedPatch: { modifiers: normalizedModifiers },
            });
          }
          if (uniqueNormalized.size !== normalized.length) {
            issues.push({
              severity: "warn",
              message: "Modifiers contain duplicates.",
              suggestedPatch: { modifiers: normalizedModifiers },
            });
          }

          return issues;
        },
      };
    }

    export function invalidModifier<TItem extends Item>(options?: {
      collapseWhitespace?: boolean;
      vocabulary?: readonly string[];
    }): RowRule<TItem> {
      const collapseWhitespace = options?.collapseWhitespace ?? true;
      const vocabulary = new Set(options?.vocabulary ?? CORE_FOOD_MODIFIER_VOCABULARY);

      return {
        id: "food.invalid-modifier",
        check: ({ item }) => {
          const modifiers = normalizeModifiers(item.modifiers, {
            collapseWhitespace,
            normalizeCase: true,
          });
          const issues: DataIntegrity.RuleIssue<TItem, Patch>[] = [];
          for (const modifier of modifiers) {
            if (vocabulary.has(modifier)) {
              continue;
            }
            issues.push({
              severity: "error",
              message: `Modifier '${modifier}' is not in Food.modifierSchema vocabulary.`,
            });
          }
          return issues;
        },
      };
    }

    export function modifierWordRedundancy<TItem extends Item>(options?: {
      collapseWhitespace?: boolean;
      redundancyMatchers?: Readonly<Record<string, RegExp>>;
    }): RowRule<TItem> {
      const collapseWhitespace = options?.collapseWhitespace ?? true;
      const redundancyMatchers =
        options?.redundancyMatchers ??
        ({
          frozen: /\bfrozen\b/i,
          canned: /\bcanned\b/i,
          dried: /\bdried\b/i,
          smoked: /\bsmoked\b/i,
          cooked: /\bcooked\b/i,
          raw: /\braw\b/i,
          fried: /\bfried\b/i,
          baked: /\bbaked\b/i,
          boiled: /\bboiled\b/i,
          roasted: /\broasted\b/i,
          steamed: /\bsteamed\b/i,
        } as const);

      return {
        id: "food.modifier-word-redundancy",
        check: ({ item }) => {
          const modifiers = normalizeModifiers(item.modifiers, {
            collapseWhitespace,
            normalizeCase: true,
          });
          const issues: DataIntegrity.RuleIssue<TItem, Patch>[] = [];
          for (const modifier of modifiers) {
            const matcher = redundancyMatchers[modifier];
            if (!matcher || !matcher.test(item.name)) {
              continue;
            }
            issues.push({
              severity: "warn",
              message: `Name redundantly includes '${modifier}' even though '${modifier}' is already a modifier.`,
            });
          }
          return issues;
        },
      };
    }

    export function normalizedLabelCollision<TItem extends Item>(options?: {
      collapseWhitespace?: boolean;
    }): DatasetRule<TItem> {
      const collapseWhitespace = options?.collapseWhitespace ?? true;

      return {
        id: "food.normalized-label-collision",
        check: ({ items }) => {
          const byLabelKey = new Map<string, CollisionRow<TItem>[]>();
          for (const item of items) {
            const labelKey = normalizeLabelKey(item.name, item.modifiers, {
              collapseWhitespace,
              normalizeCase: true,
            });
            const rows = byLabelKey.get(labelKey) ?? [];
            rows.push({ item, name: item.name, modifiers: item.modifiers ?? [] });
            byLabelKey.set(labelKey, rows);
          }

          const issues: DataIntegrity.DatasetRuleIssue<TItem, Patch>[] = [];
          for (const rows of byLabelKey.values()) {
            if (rows.length < 2) {
              continue;
            }
            for (const row of rows) {
              issues.push({
                item: row.item,
                severity: "error",
                relatedItems: rows
                  .map((candidate) => candidate.item)
                  .filter((candidate) => candidate !== row.item),
                message:
                  "Normalized label collision: same normalized name+modifiers appears on multiple rows.",
              });
            }
          }
          return issues;
        },
      };
    }

    export function mixedModifierCoverage<TItem extends Item>(options?: {
      collapseWhitespace?: boolean;
    }): DatasetRule<TItem> {
      const collapseWhitespace = options?.collapseWhitespace ?? true;
      return {
        id: "food.mixed-modifier-coverage",
        check: ({ items }) => {
          const byName = new Map<string, { item: TItem; modifiers: string[] }[]>();
          for (const item of items) {
            const normalizedName = normalizeCasefold(item.name, collapseWhitespace);
            const modifiers = normalizeModifiers(item.modifiers, {
              collapseWhitespace,
              normalizeCase: true,
            });
            const rows = byName.get(normalizedName) ?? [];
            rows.push({ item, modifiers });
            byName.set(normalizedName, rows);
          }

          const issues: DataIntegrity.DatasetRuleIssue<TItem, Patch>[] = [];
          for (const rows of byName.values()) {
            const unmodifiedRows = rows.filter((row) => row.modifiers.length === 0);
            const modifiedRows = rows.filter((row) => row.modifiers.length > 0);
            if (unmodifiedRows.length > 0 && modifiedRows.length > 0) {
              for (const row of unmodifiedRows) {
                issues.push({
                  item: row.item,
                  severity: "warn",
                  relatedItems: modifiedRows.map((candidate) => candidate.item),
                  message:
                    "Name has both modified and unmodified variants; review whether unmodified row should carry an explicit modifier.",
                });
              }
            }
          }
          return issues;
        },
      };
    }

    export function singletonWithModifier<TItem extends Item>(options?: {
      collapseWhitespace?: boolean;
    }): DatasetRule<TItem> {
      const collapseWhitespace = options?.collapseWhitespace ?? true;
      return {
        id: "food.singleton-with-modifier",
        check: ({ items }) => {
          const byName = new Map<string, { item: TItem; modifiers: string[] }[]>();
          for (const item of items) {
            const normalizedName = normalizeCasefold(item.name, collapseWhitespace);
            const modifiers = normalizeModifiers(item.modifiers, {
              collapseWhitespace,
              normalizeCase: true,
            });
            const rows = byName.get(normalizedName) ?? [];
            rows.push({ item, modifiers });
            byName.set(normalizedName, rows);
          }

          const issues: DataIntegrity.DatasetRuleIssue<TItem, Patch>[] = [];
          for (const rows of byName.values()) {
            if (rows.length !== 1) {
              continue;
            }
            const [only] = rows;
            if (!only) {
              continue;
            }
            if (only.modifiers.length === 0) {
              continue;
            }
            issues.push({
              item: only.item,
              severity: "warn",
              message:
                "Name appears only once in dataset but has a modifier; review whether modifier is necessary.",
            });
          }
          return issues;
        },
      };
    }
  }

  export function createRuleSet<TItem extends Item>(): DataIntegrity.RuleSet<TItem, RuleId, Patch> {
    return DataIntegrity.createRuleSet<TItem, RuleId, Patch>({
      rowRules: [
        Rules.nameNotEmpty(),
        Rules.bannedToken(),
        Rules.nonAscii(),
        Rules.forbiddenSymbol(),
        Rules.invalidModifier(),
        Rules.duplicateModifier(),
        Rules.modifierWordRedundancy(),
        Rules.titleCaseMismatch(),
      ],
      datasetRules: [
        Rules.normalizedLabelCollision(),
        Rules.mixedModifierCoverage(),
        Rules.singletonWithModifier(),
      ],
    });
  }
}
